import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";
import { useHistory } from "react-router";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";

import { ReactComponent as EditIcon } from "assets/img/svg/Edit2.svg";
import { ReactComponent as DeleteIcon } from "assets/img/svg/Delete.svg";
import { ReactComponent as CrossIcon } from "assets/img/svg/CrossIcon.svg";

import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import { numberDecimalLimitRegex } from "views/pages/manage/unAssigned/collectPayment/selectPaymentMode/components/constant";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import { hasApproverAccess } from "./utils";

const StudentsMappedTable = ({
  stream,
  currApprovalStatus,
  students,
  totalAmount,
  setTotalAmt,
  userRole,
  approvalConfigs,
  setRows,
  page = 0,
  pageSize = 10,
  rowCount = 0,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) => {
  const history = useHistory();
  const canEdit =
    currApprovalStatus === "L3_APPROVED"
      ? hasApproverAccess(approvalConfigs, userRole, "LEVEL_4")
      : false;
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowForm, setRowForm] = useState({});

  // PSID link logic
  const handlePsidClick = (psid) => {
    window.open(
      history.createHref({
        pathname: `admin/edp/studentDetails/${psid}`,
      }),
      "_blank",
    );
  };

  // Edit handlers
  const handleEditClick = (row) => {
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [row.id]: { mode: GridRowModes.Edit, fieldToFocus: "amount" },
    }));
    setRowForm(row);
  };

  const handleSaveClick = (rowId) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === rowId ? { ...row, ...rowForm } : row)),
    );
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
    setRowForm({});
  };

  const handleCancelClick = (rowId) => {
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
    setRowForm({});
  };

  // Delete handler
  const handleDeleteClick = (rowId) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
    setRowModesModel((oldModel) => {
      const newModel = { ...oldModel };
      delete newModel[rowId];
      return newModel;
    });
    setRowForm({});
  };

  // Field change
  const handleFieldChange = (key, value) => {
    setRowForm((prev) => ({ ...prev, [key]: value }));
  };

  // Columns
  const columns = [
    new AGridColDef("psid", "PSID")
      .setMinWidth(120)
      .renderCellComponent((params) =>
        params?.value ? (
          <a
            href="#"
            className="text-primary text-bold"
            onClick={() => handlePsidClick(params.value)}
          >
            {params.value}
          </a>
        ) : (
          <div>-</div>
        ),
      ),
    new AGridColDef("admissionSource", "Admission Source")
      .setMinWidth(120)
      .renderCellComponent((params) =>
        params?.value ? <span>{params.value}</span> : <div>-</div>,
      ),
    new AGridColDef("applicationId", "Application ID")
      .setMinWidth(120)
      .renderCellComponent((params) =>
        params?.value ? <span>{params.value}</span> : <div>-</div>,
      ),
    new AGridColDef("studentName", "Student Name")
      .setMinWidth(150)
      .renderCellComponent((params) =>
        params?.value ? <span>{params.value}</span> : <div>-</div>,
      ),
    new AGridColDef("programAction", "Program")
      .setMinWidth(100)
      .renderCellComponent((params) =>
        params?.value ? <span>{params.value}</span> : <div>-</div>,
      ),
    new AGridColDef("amount", "Payment Amount")
      .setMinWidth(120)
      .editable(false)
      .renderCellComponent((params) => formattedPrice(params.value ?? 0))
      .renderEditCellComponent((params) => (
        <AInput
          placeholder="Enter"
          value={rowForm?.amount}
          handler={(value) => {
            handleFieldChange("amount", value);
          }}
          regex={numberDecimalLimitRegex(8, 2)}
        />
      )),
  ];

  if (canEdit) {
    columns.push(
      new AGridColDef("actions", "Actions", false)
        .setMinWidth(180)
        .pinColumnLast(true)
        .renderCellComponent((params) => {
          const isEditing =
            rowModesModel[params.row.id]?.mode === GridRowModes.Edit;
          if (isEditing) {
            return (
              <>
                <AButton
                  size="xs"
                  variant="primary_filled"
                  onClick={() => handleSaveClick(params.row.id)}
                  disabled={loading}
                  className="no-ml-btn"
                >
                  Save
                </AButton>
                <button
                  onClick={() => handleCancelClick(params.row.id)}
                  className="btn btn-sm"
                  title="Cancel"
                  disabled={loading}
                >
                  <CrossIcon />
                </button>
              </>
            );
          }
          return (
            <>
              {/* <button
                onClick={() => handleEditClick(params.row)}
                className="btn btn-sm"
                title="Edit"
                disabled={
                  Object.values(rowModesModel).some(
                    (m) => m.mode === GridRowModes.Edit,
                  ) || loading
                }
              >
                <EditIcon />
              </button> */}
              <button
                onClick={() => handleDeleteClick(params.row.id)}
                className="btn btn-sm"
                title="Delete"
                disabled={
                  Object.values(rowModesModel).some(
                    (m) => m.mode === GridRowModes.Edit,
                  ) || loading
                }
              >
                <DeleteIcon />
              </button>
            </>
          );
        }),
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-bold">Students mapped - {stream || "-"}</div>

        <div className="text-bold text-success d-flex align-items-center">
          Total Amount:{" "}
          {canEdit ? (
            <AInput
              placeholder="Enter"
              value={totalAmount}
              handler={(value) => {
                setTotalAmt(value);
              }}
              regex={numberDecimalLimitRegex(8, 2)}
              className="ml-2"
            />
          ) : (
            formattedPrice(totalAmount)
          )}
        </div>
      </div>
      <ADataGrid
        rows={students}
        columns={columns || []}
        rowId={(row) => row.id}
        height="300px"
        pagination
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rowModesModel={rowModesModel}
        pinLastColumn
      />
    </>
  );
};

export default StudentsMappedTable;
